import * as React from 'react';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { modalTypes } from '@/enums';
import { openBuyNowModal } from '@/redux/modules/modal';
import { mergedCaterwaulAndHammerUIThemes as theme } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import { useTrackBuyNowButtonPressed } from '@/hooks/kameleoon/tracking/useTrackBuyNowButtonPressed';
import { useTrackBuyNowPressedAnalytics } from '@/utils/amplitude/analytics';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import ItemSaveContainer from '@/components/ItemSave/ItemSaveContainer';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import styled from 'styled-components';

type Props = {
    itemId: number;
};
const BuyNowComponent = ({ itemId }: Props) => {
    const dispatch = useAppDispatch();
    const { buyNowPrice, currency = 'USD', highBidEstimate } = useAppSelector((state) => getItemSummary(state, itemId));

    const trackBuyNowPressedAnalytics = useTrackBuyNowPressedAnalytics(itemId);
    const trackBuyNowButtonPressedExperiment = useTrackBuyNowButtonPressed();

    const showBuyNowModal = useCallback(() => {
        trackBuyNowButtonPressedExperiment();
        trackBuyNowPressedAnalytics(false);
        dispatch(
            openBuyNowModal({
                itemId: itemId,
                modalType: modalTypes.BUY_NOW,
                offerPrice: buyNowPrice,
            })
        );
    }, [buyNowPrice, dispatch, itemId, trackBuyNowButtonPressedExperiment, trackBuyNowPressedAnalytics]);

    const openMakeOfferModal = useCallback(() => {
        trackBuyNowButtonPressedExperiment();
        trackBuyNowPressedAnalytics(true);
        dispatch(
            openBuyNowModal({
                itemId: itemId,
                modalType: modalTypes.MAKE_OFFER,
            })
        );
    }, [dispatch, itemId, trackBuyNowButtonPressedExperiment, trackBuyNowPressedAnalytics]);

    return (
        <BuyNowContainer>
            <PricesContainer>
                <BuyNowPrice>
                    <FormattedCurrency
                        sourceCurrency={currency}
                        value={buyNowPrice}
                    />
                </BuyNowPrice>
                <HighBidEstimate>
                    <FormattedCurrency
                        noFraction
                        sourceCurrency={currency}
                        value={highBidEstimate}
                    />
                </HighBidEstimate>
            </PricesContainer>
            <ButtonsContainer>
                <ShouldLogInContainer
                    requireCompleteProfile
                    requireLogin
                    requireVerifiedAddress
                    runFunction={showBuyNowModal}
                >
                    <Button
                        className="mt-[14px] w-full"
                        style="primary"
                        variant="filled"
                    >
                        <FormattedMessage id="buy_now" />
                    </Button>
                </ShouldLogInContainer>
                <ShouldLogInContainer
                    requireCompleteProfile
                    requireLogin
                    requireVerifiedAddress
                    runFunction={openMakeOfferModal}
                >
                    <Button
                        className="mt-[14px] w-full"
                        style="primary"
                        variant="outlined"
                    >
                        <FormattedMessage id="make_an_offer" />
                    </Button>
                </ShouldLogInContainer>
                <ItemSaveContainer
                    fire
                    itemId={itemId}
                    showSavedCount
                />
            </ButtonsContainer>
        </BuyNowContainer>
    );
};

export default React.memo<Props>(BuyNowComponent);

const BuyNowContainer = styled.div`
    margin: 10px 0 10px;

    @media (max-width: 767px) {
        padding-bottom: 24px;
        margin-bottom: 24px;
    }
`;

const PricesContainer = styled.div`
    display: flex;
    flex-direction: row;
    font-family: var(--font-secondary);
    font-size: 25px;
    font-weight: normal;
    letter-spacing: 0.5px;
    line-height: 25px;
    margin-bottom: 16px;

    @media (max-width: 767px) {
        margin-bottom: 8px;
    }
`;

const BuyNowPrice = styled.div`
    color: ${theme.colors.black};
`;

const HighBidEstimate = styled.div`
    color: ${theme.colors.grey300};
    margin-left: 20px;
    text-decoration: line-through;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 313px;
    margin-bottom: 30px;

    @media (max-width: ${theme.breakpoints.xsMax}) {
        margin-bottom: 15px;
    }
`;
